<template>
  <b-navbar toggleable type="light" variant="light" class="p-3">
    <div class="container">
      <b-navbar-brand class="title">
        {{ t("title") }}
      </b-navbar-brand>
    </div>
  </b-navbar>
  <div v-if="loader" class="container-fluid message-container-align">
    <b-spinner
      style="width: 6rem; height: 6rem; color: #003778"
      label="Large Spinner"
    ></b-spinner>
  </div>
  <div
    v-if="errorFlag || invalidIDFlag || unAuthorizedFlag"
    class="container-fluid message-container-align"
  >
    <h2 v-if="unAuthorizedFlag">{{ t("form.unauthorized") }}</h2>
    <h2 v-if="errorFlag && !unAuthorizedFlag">
      {{ t("form.unabletoprocesstherequest") }}
    </h2>
    <h2 v-if="invalidIDFlag">{{ t("form.invalidrequest") }}</h2>
  </div>
  <b-modal
    v-model="modalVisible"
    :title="t('download.downloadstatus')"
    :ok-title="t('download.ok')"
    :cancel-title="t('download.cancel')"
  >
    <p class="text-center">{{ modalMessage }}</p>
  </b-modal>

  <div v-if="!loader && !errorFlag && !invalidIDFlag && !unAuthorizedFlag">
    <div
      class="container-fluid"
      v-if="maxDownloadFlag || remainingDownloads <= 0"
    >
      <div class="centered-container">
        <h2>{{ t("download.maxDownloadsExceeded") }}</h2>
      </div>
    </div>
    <div
      class="container-fluid"
      v-if="!maxDownloadFlag && remainingDownloads > 0"
    >
      <div class="centered-container">
        <b-button pill class="custom-button" @click="triggerFileDownload">
          {{ t("download.download") }}
        </b-button>
        <div class="spacer15"></div>
        <span id="previousDownloads" class="txtSize15">
          {{ t("download.previousdownloads") }}: {{ downloadCount }}
        </span>
        <div class="spacer5"></div>
        <span id="remainingDownloads" class="txtSize15">
          {{ t("download.remainingdownloads") }}: {{ remainingDownloads }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import axios from "axios";
import { generateChecksum } from "../services/generateChecksum";
import { decryptData } from "../services/decryptData";
import CryptoJS from "crypto-js";
import { useStore } from "vuex";
const invalidIDFlag = ref(false);
const loader = ref(false);
const route = useRoute();
const store = useStore();
const { t } = useI18n();
const modalVisible = ref(false);
const maxDownloadFlag = ref(false);
const modalMessage = ref("");
const invoiceDetail = ref<any | null>(null);
const maxDownload = computed(() => userDocument?.value?.MaxDownloads);
const downloadCount = computed(() => invoiceDetail?.value?.DownloadCount);
const secretKey = process.env.VUE_APP_ENCRYPT_DECRYPT_SECRET_KEY;
const JwtSecretKey = process.env.VUE_APP_JWT_KEY;
const remainingDownloads = computed(
  () => maxDownload.value - downloadCount.value
);
const userDocument = ref<any | null>(null);
const baseUrl = process.env.VUE_APP_BASE_API_URL;
const errorFlag = ref(false);
const unAuthorizedFlag = ref(false);
const userId = ref(route.query.p as string);
const fileName = computed(() => userDocument?.value?.FileName);
const generateUrl = async () => {
  loader.value = true;
  let token = getTokenFromSessionStorage();

  if (!token) {
    token = await getToken(token);
  }

  try {
    const encryptedDataValue = {
      FolderName: userId.value,
      FileName: fileName.value,
      timestamp: new Date().toISOString(),
    };
    const checksumString = Object.values(encryptedDataValue).join("-");
    const checksum = generateChecksum(checksumString);
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const response = await axios.post(
      `${baseUrl}/url/generate-url`,
      { encryptedDataString },
      { headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum } }
    );
    const encryptedData = response.data;
    const decryptedText = decryptData(encryptedData);
    const invoiceData = JSON.parse(decryptedText);
    const receivedChecksum = response.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(response.data);
    if (
      receivedChecksum === calculatedChecksum &&
      invoiceData.UserId === userId.value
    ) {
      url.value = invoiceData.filePath;
    } else {
      errorFlag.value = true;
      loader.value = false;
    }
    return true;
  } catch (error: any) {
    loader.value = false;
    errorFlag.value = true;
    if (error.response) {
      console.error(
        "Error generating URL:",
        error.response?.data?.message || "Unknown error"
      );
      return false;
    } else if (error.request) {
      console.error(
        "No response received. Please check your network connection."
      );
      return false;
    } else {
      console.error("Error in generating URL:", error.message);
      return false;
    }
  }
};
const url = ref("");
const triggerFileDownload = async () => {
  const urlGenerated = await generateUrl();
  if (urlGenerated) {
    if (url.value) {
      const link = document.createElement("a");
      link.href = url.value;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showModal(t("download.PDFdownloadedsuccessfully"));
      fetchUserData();
      loader.value = false;
    } else {
      showModal(t("download.PDFdownloadedFailure"));
      console.error("No signed URL available for download.");
    }
  }
};

const getTokenFromSessionStorage = () => {
  const token = sessionStorage.getItem(`token_${userId.value}`);
  const expirationTime = parseInt(
    sessionStorage.getItem(`tokenExpiration_${userId.value}`) || "0"
  );

  if (Date.now() > expirationTime) {
    sessionStorage.removeItem(`token_${userId.value}`);
    sessionStorage.removeItem(`tokenExpiration_${userId.value}`);
    return null;
  }
  return token;
};

const showModal = (message: string) => {
  modalMessage.value = message;
  modalVisible.value = true;
};
const setTokenInSessionStorage = (token: any, expiresIn: number) => {
  const expirationTime = Date.now() + expiresIn * 3600 * 1000;
  sessionStorage.setItem(`token_${userId.value}`, token);
  sessionStorage.setItem(
    `tokenExpiration_${userId.value}`,
    expirationTime.toString()
  );
};
const getInvoiceCount = async (token: any) => {
  loader.value = true;
  const checksumString = `${userId.value}`;
  const checksum = generateChecksum(checksumString);
  try {
    const invoiceCountDetail = await axios.get(
      `${baseUrl}/invoice/count/${userId.value}`,
      {
        headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
      }
    );
    const encryptedData = invoiceCountDetail.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = invoiceCountDetail.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(invoiceCountDetail.data);
    const invoiceData = JSON.parse(decryptedText);
    if (
      receivedChecksum === calculatedChecksum &&
      invoiceData.UserId === userId.value
    ) {
      invoiceDetail.value = invoiceData;
      loader.value = false;
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
  } catch (error: any) {
    loader.value = false;
    errorFlag.value = true;
    console.error("Failed to fetch invoice details:", error);
  }
};
const getToken = async (token: any) => {
  token = "";
  try {
    const encryptedDataValue = {
      UserId: userId.value,
      SecretKey: JwtSecretKey,
      timestamp: new Date().toISOString(),
    };
    const checksumString = Object.values(encryptedDataValue).join("-");
    const checksum = generateChecksum(checksumString);
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const tokenResponse = await axios.post(
      `${baseUrl}/auth/generate-token`,
      { encryptedDataString },
      {
        headers: {
          "Content-Type": "application/json",
          "X-Checksum": checksum,
        },
      }
    );
    token = tokenResponse.data.token;
    setTokenInSessionStorage(token, 23);
    return token;
  } catch (error: any) {
    if (error.response) {
      if (error.response.status === 404 || error.response.status === 401) {
        unAuthorizedFlag.value = true;
      }
    }
    errorFlag.value = true;
    throw new Error("Failed to generate token. Please try again.");
  }
};
const fetchUserData = async () => {
  try {
    loader.value = true;
    let token = getTokenFromSessionStorage();

    if (!token) {
      token = await getToken(token);
    }
    const checksumString = `${userId.value}`;
    const checksum = generateChecksum(checksumString);
    try {
      const invoiceResponse = await axios.get(
        `${baseUrl}/invoice/${userId.value}`,
        {
          headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
        }
      );
      const encryptedData = invoiceResponse.data;
      const decryptedText = decryptData(encryptedData);
      const receivedChecksum = invoiceResponse.headers["x-file-checksum"];
      const calculatedChecksum = generateChecksum(invoiceResponse.data);
      const invoiceData = JSON.parse(decryptedText);

      if (
        receivedChecksum === calculatedChecksum &&
        invoiceData.UserId === userId.value
      ) {
        userDocument.value = invoiceData;
        loader.value = false;
      } else {
        loader.value = false;
        errorFlag.value = true;
      }
    } catch (error: any) {
      console.error("Failed to fetch user data", error);
      if (error.response && error.response.status === 404) {
        throw new Error(
          "User ID not found. Please check the ID and try again."
        );
      } else {
        errorFlag.value = true;
        throw new Error("Failed to fetch user data. Please try again later.");
      }
    }
    getInvoiceCount(token);
  } catch (error: any) {
    error.value =
      error.message || "An unexpected error occurred. Please try again later.";
    loader.value = false;
  }
};
onMounted(() => {
  if (store.state.isAuth) {
    fetchUserData();
  } else {
    loader.value = false;
    invalidIDFlag.value = true;
  }
});
</script>

<script lang="ts">
export default {
  name: "PDFDownloadComponent",
};
</script>

<style scoped>
@media screen and (max-width: 767px) {
  .title {
    font-size: 20px !important;
    margin-left: -15px;
  }
}
.title {
  font-family: "bellslim_mediumregular", Helvetica, Arial, sans-serif;
  font-size: 25px;
  font-weight: bold;
}
.custom-button {
  color: #fff;
  background-color: #003778;
  border: 2px solid #003778;
  font-size: 15px;
  padding: 10px 32px;
  text-align: center;
  cursor: pointer;
  border-radius: 24px;
  line-height: 1;
  --bs-btn-hover-bg: #003778;
}

.txtSize15 {
  font-size: 15px;
}

.spacer15 {
  padding-top: 15px;
}

.spacer5 {
  padding-top: 5px;
}

.centered-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 356px;
}

.exceeded-message {
  color: red;
  font-weight: bold;
  margin-top: 10px;
}
.message-container-align {
  min-height: 363px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
